import type { FormEvent } from 'react'

import { Info } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { Option as MultipleSelectorOption } from '@/components/ui/multiple-selector'
import type {
  LeaveEntitlementRuleCreateDto,
  LeaveEntitlementRuleCreateDtoSupervisionLevelsItem,
  LeaveEntitlementRuleCreateDtoUserType,
  LeaveTypeConfigDto,
  LeaveTypeDto,
  ListLeaveTypesParams,
} from '@/utils/api/leave-service/models'
import type { ListDepartmentsParams } from '@/utils/api/user-service/models'

import { OrganisationSearchInput } from '@/components/common/OrganisationSearchInput'
import SearchSelect from '@/components/search-select'
import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { AnimatedSwitch } from '@/components/ui/animated-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import MultipleSelector from '@/components/ui/multiple-selector'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAuth } from '@/features/authentication/components/auth-context'
import { isAdminUser } from '@/lib/role-guards'
import {
  useDeleteLeaveType,
  useListAllLeaveTypes,
  useUpsertLeaveType,
} from '@/utils/api/leave-service'
import { LeaveEntitlementRuleCreateDtoSupervisionLevelsItem as LeaveEntitlementRuleCreateDtoSupervisionLevels } from '@/utils/api/leave-service/models'
import { useGetAllOrganisations, useListDepartments } from '@/utils/api/user-service'

const DEFAULT_FORM_STATE = {
  id: '',
  code: '',
  name: '',
  description: '',
  noticePeriodDays: '',
  requiresAttachment: false,
  organisationId: '',
}

const SUPERVISION_LEVEL_LABELS: Record<string, string> = {
  0: 'Non-supervisor',
  1: 'Manager',
  2: 'Director',
  3: 'HR',
  4: 'CEO',
}

interface EntitlementRuleFormState {
  rowId: string
  id?: number
  userType: LeaveEntitlementRuleCreateDtoUserType | undefined
  supervisionLevels: LeaveEntitlementRuleCreateDtoSupervisionLevelsItem[]
  departmentId: string
  gender: 'MALE' | 'FEMALE' | undefined
  days: string
  includeWeekendsAndHolidays: boolean
  carryOverAllowed: boolean
  maxCarryoverDays: string
  carryoverExpiryMonth: string
  carryoverExpiryDay: string
  borrowingAllowed: boolean
  maxBorrowDays: string
  year: string
  isExisting: boolean
}

interface LeaveEntitlementRuleCreatePayload extends LeaveEntitlementRuleCreateDto {
  maxCarryoverDays?: number
  carryoverExpiryMonth?: number
  carryoverExpiryDay?: number
  borrowingAllowed?: boolean
  maxBorrowDays?: number
}

interface LeaveEntitlementRuleWithPolicy {
  id?: number
  userType?: LeaveEntitlementRuleCreateDtoUserType
  supervisionLevels?: LeaveEntitlementRuleCreateDtoSupervisionLevelsItem[]
  supervisionLevel?: LeaveEntitlementRuleCreateDtoSupervisionLevelsItem
  departmentId?: number
  gender?: 'MALE' | 'FEMALE'
  days?: number
  includeWeekendsAndHolidays?: boolean
  carryOverAllowed?: boolean
  year?: number
  maxCarryoverDays?: number
  carryoverExpiryMonth?: number
  carryoverExpiryDay?: number
  borrowingAllowed?: boolean
  maxBorrowDays?: number
}

interface LeaveTypeWithEntitlements extends LeaveTypeDto {
  maxCarryoverDays?: number
  carryoverExpiryMonth?: number
  carryoverExpiryDay?: number
  borrowingAllowed?: boolean
  maxBorrowDays?: number
}

interface LeaveTypeEntitlementRow {
  rowId: string
  leaveTypeId?: number
  leaveType?: LeaveTypeWithEntitlements
  code?: string
  name?: string
  noticePeriodDays?: number
  requiresAttachment?: boolean
  userType?: string
  supervisionLevel?: string
  departmentName?: string
  gender?: string
  days?: number
  includeWeekendsAndHolidays?: boolean
  carryOverAllowed?: boolean
  year?: number
  maxCarryoverDays?: number
  carryoverExpiryMonth?: number
  carryoverExpiryDay?: number
  borrowingAllowed?: boolean
  maxBorrowDays?: number
}

function createEntitlementRule(overrides: Partial<EntitlementRuleFormState> = {}): EntitlementRuleFormState {
  return {
    rowId: `rule-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    userType: undefined,
    supervisionLevels: [],
    departmentId: '',
    gender: undefined,
    days: '',
    id: undefined,
    includeWeekendsAndHolidays: false,
    carryOverAllowed: false,
    maxCarryoverDays: '',
    carryoverExpiryMonth: '3',
    carryoverExpiryDay: '31',
    borrowingAllowed: false,
    maxBorrowDays: '',
    year: '',
    isExisting: false,
    ...overrides,
  }
}

export function LeaveTypesPage() {
  const { user: currentUser } = useAuth()
  const showOrganisationFilter = isAdminUser(currentUser)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create')
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE)
  const [entitlementRules, setEntitlementRules] = useState<EntitlementRuleFormState[]>(() => [createEntitlementRule()])
  const [entitlementError, setEntitlementError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [organisationFilter, setOrganisationFilter] = useState('all')

  const leaveTypesParams = useMemo<ListLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])
  const departmentsParams = useMemo<ListDepartmentsParams>(() => ({
    page: 0,
    size: 200,
  }), [])

  const leaveTypesQuery = useListAllLeaveTypes(leaveTypesParams)
  const departmentsQuery = useListDepartments(departmentsParams)
  const organisationsQuery = useGetAllOrganisations({
    query: {
      enabled: showOrganisationFilter,
      staleTime: 30_000,
    },
  })
  const upsertMutation = useUpsertLeaveType()
  const deleteLeaveTypeMutation = useDeleteLeaveType()

  const leaveTypes = useMemo<LeaveTypeWithEntitlements[]>(
    () => leaveTypesQuery.data?.data?.content ?? [],
    [leaveTypesQuery.data],
  )
  const departments = useMemo(
    () => departmentsQuery.data?.data?.content ?? [],
    [departmentsQuery.data],
  )
  const organisations = useMemo(() => organisationsQuery.data?.data ?? [], [organisationsQuery.data])
  const canSubmit = formState.code.trim() !== '' && formState.name.trim() !== ''
  const isSaving = upsertMutation.isPending
  const isTableLoading = leaveTypesQuery.isPending || departmentsQuery.isPending
  const entitlementSupervisionLevels = useMemo(
    () => Object.values(LeaveEntitlementRuleCreateDtoSupervisionLevels),
    [],
  )
  const entitlementSupervisionLevelOptions = useMemo<MultipleSelectorOption[]>(
    () => entitlementSupervisionLevels.map(level => ({
      value: level,
      label: SUPERVISION_LEVEL_LABELS[level] ?? `Level ${level}`,
    })),
    [entitlementSupervisionLevels],
  )

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit || isSaving) {
      return
    }

    setEntitlementError(null)
    setSubmitError(null)

    const parsedNoticePeriodDays = formState.noticePeriodDays.trim() === '' ? undefined : Number(formState.noticePeriodDays)
    const preparedRules: LeaveEntitlementRuleCreatePayload[] = []
    let hasInvalidRules = false
    let invalidRulesMessage: string | null = null

    entitlementRules.forEach((rule) => {
      const normalizedUserType = rule.userType === undefined ? undefined : rule.userType
      const normalizedSupervisionLevels = rule.supervisionLevels.length > 0
        ? rule.supervisionLevels
        : undefined
      const trimmedDepartmentId = rule.departmentId.trim()
      const trimmedDays = rule.days.trim()
      const trimmedYear = rule.year.trim()
      const trimmedMaxCarryoverDays = rule.maxCarryoverDays.trim()
      const trimmedCarryoverExpiryMonth = rule.carryoverExpiryMonth.trim()
      const trimmedCarryoverExpiryDay = rule.carryoverExpiryDay.trim()
      const trimmedMaxBorrowDays = rule.maxBorrowDays.trim()
      const hasAnyValue = normalizedUserType !== undefined
        || normalizedSupervisionLevels !== undefined
        || trimmedDepartmentId !== ''
        || trimmedDays !== ''
        || rule.gender !== undefined
        || trimmedYear !== ''
        || rule.carryOverAllowed
        || trimmedMaxCarryoverDays !== ''
        || trimmedCarryoverExpiryMonth !== ''
        || trimmedCarryoverExpiryDay !== ''
        || rule.borrowingAllowed
        || trimmedMaxBorrowDays !== ''
        || rule.includeWeekendsAndHolidays

      if (!hasAnyValue) {
        return
      }

      if (trimmedDays === '') {
        hasInvalidRules = true
        invalidRulesMessage = 'Each entitlement rule needs a valid days value.'
        return
      }

      const parsedRuleDays = Number(trimmedDays)
      if (Number.isNaN(parsedRuleDays)) {
        hasInvalidRules = true
        invalidRulesMessage = 'Each entitlement rule needs a valid days value.'
        return
      }

      const parsedRuleYear = trimmedYear === '' ? undefined : Number(trimmedYear)
      if (parsedRuleYear !== undefined && Number.isNaN(parsedRuleYear)) {
        hasInvalidRules = true
        invalidRulesMessage = 'Each entitlement rule needs a valid year.'
        return
      }

      const parsedDepartmentId = trimmedDepartmentId === '' ? undefined : Number(trimmedDepartmentId)
      if (parsedDepartmentId !== undefined && Number.isNaN(parsedDepartmentId)) {
        hasInvalidRules = true
        invalidRulesMessage = 'Each entitlement rule needs a valid department.'
        return
      }

      const nextRule: LeaveEntitlementRuleCreatePayload = {
        id: rule.id,
        userType: normalizedUserType,
        supervisionLevels: normalizedSupervisionLevels,
        departmentId: parsedDepartmentId,
        gender: rule.gender?.toLocaleLowerCase() === 'any' ? undefined : rule.gender,
        days: parsedRuleDays,
        includeWeekendsAndHolidays: rule.includeWeekendsAndHolidays,
        carryOverAllowed: rule.carryOverAllowed,
        year: parsedRuleYear,
      }

      if (rule.carryOverAllowed) {
        const parsedMaxCarryoverDays = trimmedMaxCarryoverDays === '' ? undefined : Number(trimmedMaxCarryoverDays)
        if (parsedMaxCarryoverDays !== undefined && (Number.isNaN(parsedMaxCarryoverDays) || parsedMaxCarryoverDays < 0)) {
          hasInvalidRules = true
          invalidRulesMessage = 'Carryover max days must be a non-negative number.'
          return
        }

        const parsedCarryoverExpiryMonth = trimmedCarryoverExpiryMonth === '' ? 3 : Number(trimmedCarryoverExpiryMonth)
        if (Number.isNaN(parsedCarryoverExpiryMonth) || parsedCarryoverExpiryMonth < 1 || parsedCarryoverExpiryMonth > 12) {
          hasInvalidRules = true
          invalidRulesMessage = 'Carryover expiry month must be between 1 and 12.'
          return
        }

        const parsedCarryoverExpiryDay = trimmedCarryoverExpiryDay === '' ? 31 : Number(trimmedCarryoverExpiryDay)
        if (Number.isNaN(parsedCarryoverExpiryDay) || parsedCarryoverExpiryDay < 1 || parsedCarryoverExpiryDay > 31) {
          hasInvalidRules = true
          invalidRulesMessage = 'Carryover expiry day must be between 1 and 31.'
          return
        }

        const parsedMaxBorrowDays = trimmedMaxBorrowDays === '' ? undefined : Number(trimmedMaxBorrowDays)
        if (parsedMaxBorrowDays !== undefined && (Number.isNaN(parsedMaxBorrowDays) || parsedMaxBorrowDays < 0)) {
          hasInvalidRules = true
          invalidRulesMessage = 'Borrow max days must be a non-negative number.'
          return
        }
        if (rule.borrowingAllowed && parsedMaxBorrowDays === undefined) {
          hasInvalidRules = true
          invalidRulesMessage = 'Set max borrow days when borrowing is enabled.'
          return
        }

        nextRule.maxCarryoverDays = parsedMaxCarryoverDays
        nextRule.carryoverExpiryMonth = parsedCarryoverExpiryMonth
        nextRule.carryoverExpiryDay = parsedCarryoverExpiryDay
        nextRule.borrowingAllowed = rule.borrowingAllowed
        nextRule.maxBorrowDays = rule.borrowingAllowed ? parsedMaxBorrowDays : undefined
      }

      preparedRules.push(nextRule)
    })

    if (hasInvalidRules) {
      setEntitlementError(invalidRulesMessage ?? 'Please correct entitlement rule details.')
      return
    }

    const payload: LeaveTypeConfigDto = {
      id: formState.id ? Number(formState.id) : undefined,
      code: formState.code.trim() || undefined,
      name: formState.name.trim() || undefined,
      description: formState.description.trim() || undefined,
      noticePeriodDays: parsedNoticePeriodDays === undefined || Number.isNaN(parsedNoticePeriodDays)
        ? undefined
        : parsedNoticePeriodDays,
      requiresAttachment: formState.requiresAttachment,
      organisationId: formState.organisationId ? Number(formState.organisationId) : undefined,
      entitlements: preparedRules,
    }

    try {
      await upsertMutation.mutateAsync({ data: payload })
      setFormState(DEFAULT_FORM_STATE)
      setEntitlementRules([createEntitlementRule()])
      setDialogMode('create')
      setDialogOpen(false)
      void leaveTypesQuery.refetch()
    }
    catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Unable to save leave type.')
    }
  }

  const handleAddRule = useCallback(() => {
    setEntitlementRules(prev => [...prev, createEntitlementRule()])
  }, [])

  const handleRemoveRule = useCallback((rowId: string) => {
    setEntitlementRules(prev => prev.filter(rule => rule.rowId !== rowId))
  }, [])

  const handleRuleChange = useCallback((rowId: string, updates: Partial<EntitlementRuleFormState>) => {
    setEntitlementRules(prev => prev.map(rule => (rule.rowId === rowId ? { ...rule, ...updates } : rule)))
  }, [])

  const openCreateDialog = useCallback(() => {
    setFormState(DEFAULT_FORM_STATE)
    setEntitlementRules([createEntitlementRule()])
    setEntitlementError(null)
    setSubmitError(null)
    setDialogMode('create')
    setDialogOpen(true)
  }, [])

  const handleEdit = useCallback((item: LeaveTypeWithEntitlements) => {
    const policy = item.carryoverPolicy

    setFormState({
      id: item.id?.toString() ?? '',
      code: item.code ?? '',
      name: item.name ?? '',
      description: item.description ?? '',
      noticePeriodDays: item.noticePeriodDays?.toString() ?? '',
      requiresAttachment: Boolean(item.requiresAttachment),
      organisationId: item.organisationId?.toString() ?? '',
    })

    const matchingEntitlements = item.entitlements ?? []
    const nextRules = matchingEntitlements.length > 0
      ? matchingEntitlements.map((entitlement) => {
        const entitlementPolicy = entitlement as LeaveEntitlementRuleWithPolicy
        const maxCarryoverDays = entitlementPolicy.maxCarryoverDays ?? policy?.maxCarryoverDays ?? item.maxCarryoverDays
        const carryoverExpiryMonth = entitlementPolicy.carryoverExpiryMonth ?? policy?.carryoverExpiryMonth ?? item.carryoverExpiryMonth ?? 3
        const carryoverExpiryDay = entitlementPolicy.carryoverExpiryDay ?? policy?.carryoverExpiryDay ?? item.carryoverExpiryDay ?? 31
        const borrowingAllowed = entitlementPolicy.borrowingAllowed ?? policy?.borrowingAllowed ?? item.borrowingAllowed ?? false
        const maxBorrowDays = entitlementPolicy.maxBorrowDays ?? policy?.maxBorrowDays ?? item.maxBorrowDays
        const levelsFromPayload = [
          ...((entitlement.supervisionLevels as Array<string | number | undefined>) ?? []),
          ...(entitlementPolicy.supervisionLevel ? [entitlementPolicy.supervisionLevel] : []),
        ]
        const normalizedSupervisionLevels = Array.from(
          new Set(
            levelsFromPayload
              .map(level => level?.toString())
              .filter(
                (level): level is LeaveEntitlementRuleCreateDtoSupervisionLevelsItem =>
                  Boolean(level) && entitlementSupervisionLevels.includes(level as LeaveEntitlementRuleCreateDtoSupervisionLevelsItem),
              ),
          ),
        )

        return createEntitlementRule({
          userType: (entitlement.userType as LeaveEntitlementRuleCreateDtoUserType | undefined) ?? undefined,
          supervisionLevels: normalizedSupervisionLevels,
          departmentId: entitlement.departmentId?.toString() ?? '',
          gender: entitlement.gender ?? undefined,
          days: entitlement.days?.toString() ?? '',
          includeWeekendsAndHolidays: Boolean(entitlement.includeWeekendsAndHolidays),
          carryOverAllowed: Boolean(entitlement.carryOverAllowed),
          maxCarryoverDays: maxCarryoverDays?.toString() ?? '',
          carryoverExpiryMonth: carryoverExpiryMonth.toString(),
          carryoverExpiryDay: carryoverExpiryDay.toString(),
          borrowingAllowed: Boolean(borrowingAllowed),
          maxBorrowDays: maxBorrowDays?.toString() ?? '',
          year: entitlement.year?.toString() ?? '',
          id: entitlement.id,
          isExisting: true,
        })
      })
      : [createEntitlementRule()]

    setEntitlementRules(nextRules)
    setEntitlementError(null)
    setSubmitError(null)
    setDialogMode('edit')
    setDialogOpen(true)
  }, [entitlementSupervisionLevels])

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setDialogMode('create')
      setFormState(DEFAULT_FORM_STATE)
      setEntitlementRules([createEntitlementRule()])
      setEntitlementError(null)
      setSubmitError(null)
    }
  }, [])

  const handleDeleteLeaveType = useCallback(async (leaveType?: LeaveTypeWithEntitlements) => {
    if (!leaveType?.id || deleteLeaveTypeMutation.isPending) {
      return
    }
    try {
      const response = await deleteLeaveTypeMutation.mutateAsync({
        params: { id: leaveType.id },
      })
      if (response?.data?.success === false) {
        throw new Error(response.data.message || 'Unable to delete leave type.')
      }
      toast.success(response?.data?.message || 'Leave type deleted.')
      void leaveTypesQuery.refetch()
    }
    catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to delete leave type.')
    }
  }, [deleteLeaveTypeMutation, leaveTypesQuery])

  const dialogTitle = dialogMode === 'create' ? 'Create Leave Type' : 'Edit Leave Type'
  const dialogDescription = dialogMode === 'create'
    ? 'Add a new leave type and its entitlement rules.'
    : 'Update the selected leave type and entitlement rules.'
  const submitLabel = dialogMode === 'create' ? 'Create Leave Type' : 'Save Changes'

  const tableRows = useMemo<LeaveTypeEntitlementRow[]>(() => {
    const rows: LeaveTypeEntitlementRow[] = []

    leaveTypes.forEach((leaveType) => {
      const leaveTypeEntitlements = leaveType.entitlements ?? []
      if (leaveTypeEntitlements.length === 0) {
        const maxCarryoverDays = leaveType.carryoverPolicy?.maxCarryoverDays ?? leaveType.maxCarryoverDays
        const carryoverExpiryMonth = leaveType.carryoverPolicy?.carryoverExpiryMonth ?? leaveType.carryoverExpiryMonth
        const carryoverExpiryDay = leaveType.carryoverPolicy?.carryoverExpiryDay ?? leaveType.carryoverExpiryDay
        const borrowingAllowed = leaveType.carryoverPolicy?.borrowingAllowed ?? leaveType.borrowingAllowed
        const maxBorrowDays = leaveType.carryoverPolicy?.maxBorrowDays ?? leaveType.maxBorrowDays

        rows.push({
          rowId: `leave-type-${leaveType.id ?? leaveType.code ?? leaveType.name ?? 'unknown'}`,
          leaveTypeId: leaveType.id,
          leaveType,
          code: leaveType.code,
          name: leaveType.name,
          noticePeriodDays: leaveType.noticePeriodDays,
          requiresAttachment: leaveType.requiresAttachment,
          maxCarryoverDays,
          carryoverExpiryMonth,
          carryoverExpiryDay,
          borrowingAllowed,
          maxBorrowDays,
        })
        return
      }

      leaveTypeEntitlements.forEach((entitlement, index) => {
        const departmentName = String(
          departments.find(department => department.id === entitlement.departmentId)?.name ?? '',
        )

        const maxCarryoverDays = leaveType.carryoverPolicy?.maxCarryoverDays ?? leaveType.maxCarryoverDays
        const carryoverExpiryMonth = leaveType.carryoverPolicy?.carryoverExpiryMonth ?? leaveType.carryoverExpiryMonth
        const carryoverExpiryDay = leaveType.carryoverPolicy?.carryoverExpiryDay ?? leaveType.carryoverExpiryDay
        const borrowingAllowed = leaveType.carryoverPolicy?.borrowingAllowed ?? leaveType.borrowingAllowed
        const maxBorrowDays = leaveType.carryoverPolicy?.maxBorrowDays ?? leaveType.maxBorrowDays
        const entitlementPolicy = entitlement as LeaveEntitlementRuleWithPolicy
        const supervisionLevelLabel = Array.from(
          new Set([
            ...((entitlement.supervisionLevels as Array<string | number | undefined>) ?? []),
            ...(entitlementPolicy.supervisionLevel ? [entitlementPolicy.supervisionLevel] : []),
          ]
            .map(level => level?.toString())
            .filter((level): level is string => Boolean(level))),
        )
          .map(level => SUPERVISION_LEVEL_LABELS[level] ?? level)
          .join(', ')

        rows.push({
          rowId: `entitlement-${entitlement.id ?? `${leaveType.id ?? leaveType.code ?? leaveType.name ?? 'unknown'}-${index}`}`,
          leaveTypeId: leaveType.id,
          leaveType,
          code: leaveType.code,
          name: leaveType.name,
          noticePeriodDays: leaveType.noticePeriodDays,
          requiresAttachment: leaveType.requiresAttachment,
          userType: entitlement.userType,
          supervisionLevel: supervisionLevelLabel,
          departmentName,
          gender: entitlement.gender,
          days: entitlement.days,
          includeWeekendsAndHolidays: entitlement.includeWeekendsAndHolidays,
          carryOverAllowed: entitlement.carryOverAllowed,
          year: entitlement.year,
          maxCarryoverDays,
          carryoverExpiryMonth,
          carryoverExpiryDay,
          borrowingAllowed,
          maxBorrowDays,
        })
      })
    })

    return rows
  }, [departments, leaveTypes])
  const filteredTableRows = useMemo(() => {
    if (!showOrganisationFilter || organisationFilter === 'all') {
      return tableRows
    }
    const parsedOrganisationId = Number(organisationFilter)
    if (Number.isNaN(parsedOrganisationId)) {
      return tableRows
    }
    return tableRows.filter(row => row.leaveType?.organisationId === parsedOrganisationId)
  }, [organisationFilter, showOrganisationFilter, tableRows])

  const initialColumnVisibility = useMemo(() => ({
    userType: false,
    departmentName: false,
    gender: false,
    maxBorrowDays: false,
    borrowingAllowed: false,
    carryoverExpiryDay: false,
    maxCarryoverDays: false,
    organisation: showOrganisationFilter,
    includeWeekendsAndHolidays: false,
    year: false,
  }), [showOrganisationFilter])

  const columns = useMemo<ColumnConfig<LeaveTypeEntitlementRow>[]>(() => ([
    {
      key: 'code',
      header: 'Code',
    },
    {
      key: 'name',
      header: 'Name',
    },
    {
      key: 'noticePeriodDays',
      header: 'Notice Period (Days)',
      type: 'number',
      cell: (value) => {
        if (value === 0) {
          return 'Immediate'
        }

        return value === undefined ? '-' : new Intl.NumberFormat().format(Number(value))
      },
    },
    {
      key: 'requiresAttachment',
      header: 'Attachment',
      type: 'boolean',
    },
    {
      key: 'maxCarryoverDays',
      header: 'Max Carryover',
      type: 'number',
      cell: (value) => {
        if (value === undefined || value === null) {
          return 'No cap'
        }
        return `${new Intl.NumberFormat().format(Number(value))} days`
      },
    },
    {
      key: 'carryoverExpiryDay',
      header: 'Carryover Expiry',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const month = row.carryoverExpiryMonth ?? 3
        const day = row.carryoverExpiryDay ?? 31
        const monthLabel = new Date(2000, Math.max(month - 1, 0), 1).toLocaleString(undefined, { month: 'long' })
        return `${day} ${monthLabel}`
      },
    },
    {
      key: 'borrowingAllowed',
      header: 'Borrowing',
      type: 'boolean',
      cell: value => (value ? 'Enabled' : 'Disabled'),
    },
    {
      key: 'maxBorrowDays',
      header: 'Max Borrow Days',
      type: 'number',
      cell: (value, row) => {
        if (!row.borrowingAllowed) {
          return 'Not allowed'
        }
        return `${new Intl.NumberFormat().format(Number(value) || 0)} days`
      },
    },
    {
      key: 'userType',
      header: 'User Type',
      cell: (value) => {
        const normalized = value ? String(value) : ''
        return normalized ? normalized.replaceAll('_', ' ') : '-'
      },
    },
    {
      key: 'supervisionLevel',
      header: 'Supervision Level',
      cell: value => (value ? String(value) : '-'),
    },
    {
      key: 'organisation',
      header: 'Organisation',
      cell: (_value, row) => {
        const organisationId = row.leaveType?.organisationId
        const organisation = organisations.find(org => org.id === organisationId)
        return organisation ? organisation.name : '-'
      },
    },
    {
      key: 'departmentName',
      header: 'Department',
      cell: value => (value ? String(value) : '-'),
    },
    {
      key: 'gender',
      header: 'Gender',
      cell: (value, row) => {
        if (row.days === undefined) {
          return '-'
        }
        const normalized = typeof value === 'string' ? value : ''
        if (!normalized || normalized === 'ANY') {
          return 'Both Genders'
        }
        if (normalized === 'MALE') {
          return 'Male'
        }
        if (normalized === 'FEMALE') {
          return 'Female'
        }
        return normalized
      },
    },
    {
      key: 'days',
      header: 'Entitlement Days',
      cell: (value, row) => (row.days === undefined ? '-' : new Intl.NumberFormat().format(Number(value) || 0)),
    },
    {
      key: 'includeWeekendsAndHolidays',
      header: 'Weekends/Holidays',
      cell: (value, row) => {
        if (row.days === undefined) {
          return '-'
        }
        return value ? 'Yes' : 'No'
      },
    },
    {
      key: 'carryOverAllowed',
      header: 'Carry Over',
      cell: (value, row) => {
        if (row.days === undefined) {
          return '-'
        }
        return value ? 'Yes' : 'No'
      },
    },
    {
      key: 'year',
      header: 'Year',
      cell: (value, row) => (row.days === undefined ? '-' : new Intl.NumberFormat().format(Number(value) || 0)),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const leaveTypeLabel = row.leaveType?.name
          ?? row.leaveType?.code
          ?? (row.leaveType?.id ? `leave type ${row.leaveType.id}` : 'this leave type')
        return (
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!row.leaveType}
              onClick={() => row.leaveType && handleEdit(row.leaveType)}
            >
              Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={!row.leaveType?.id || deleteLeaveTypeMutation.isPending}
                >
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete leave type?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {`This will remove ${leaveTypeLabel}. This action cannot be undone.`}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => handleDeleteLeaveType(row.leaveType)}
                    disabled={!row.leaveType?.id || deleteLeaveTypeMutation.isPending}
                  >
                    {deleteLeaveTypeMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )
      },
    },
  ]), [deleteLeaveTypeMutation.isPending, handleDeleteLeaveType, handleEdit, organisations])

  return (
    <div className="space-y-6">
      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="w-[95vw] sm:max-w-[95vw] lg:max-w-7xl max-h-[90vh] overflow-y-auto overflow-x-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{dialogDescription}</DialogDescription>
          </DialogHeader>
          <form className="space-y-4 min-w-[920px]" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="leave-type-code">Code</Label>
                  {/* <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
                        aria-label="Leave type help"
                      >
                        <Info className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      Pick the kind of leave. It tells us which rules to use.
                    </TooltipContent>
                  </Tooltip> */}
                </div>
                <Input
                  id="leave-type-code"
                  value={formState.code}
                  onChange={event => setFormState(prev => ({ ...prev, code: event.target.value }))}
                  placeholder="ANNUAL"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label htmlFor="leave-type-name">Name</Label>
                <Input
                  id="leave-type-name"
                  value={formState.name}
                  onChange={event => setFormState(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Annual Leave"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leave-type-notice-period">Notice Period (Days)</Label>
                <Input
                  id="leave-type-notice-period"
                  type="number"
                  min={0}
                  value={formState.noticePeriodDays}
                  onChange={event => setFormState(prev => ({ ...prev, noticePeriodDays: event.target.value }))}
                  placeholder="14"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="leave-type-description">Description</Label>
                <Textarea
                  id="leave-type-description"
                  value={formState.description}
                  onChange={event => setFormState(prev => ({ ...prev, description: event.target.value }))}
                  placeholder="Optional details for this leave type."
                />
              </div>
              <div className="space-y-2">
                <div className="flex flex-col h-full gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 h-full">
                      <div>
                        <Label htmlFor="leave-type-attachment">Requires Attachment</Label>
                        <p className="text-sm text-muted-foreground">Require files when submitting.</p>
                      </div>
                      <AnimatedSwitch
                        id="leave-type-attachment"
                        checked={formState.requiresAttachment}
                        onCheckedChange={checked => setFormState(prev => ({ ...prev, requiresAttachment: checked }))}
                      />
                    </div>
                  </div>

                </div>
              </div>
              {showOrganisationFilter && (
                <div className="space-y-2">
                  <OrganisationSearchInput
                    label="Organisation"
                    placeholder="Select an organisation (optional)"
                    value={formState.organisationId}
                    onValueChange={value => setFormState(prev => ({ ...prev, organisationId: value }))}
                    options={organisations
                      .filter(org => typeof org.id === 'number')
                      .map(org => ({
                        value: String(org.id),
                        id: org.id,
                        name: org.name ?? `Organisation ${org.id}`,
                      }))}
                    isLoading={organisationsQuery.isPending}
                    disabled={organisationsQuery.isPending || organisationsQuery.isError}
                  />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <hr className="mt-10 mb-4 text-secondary" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="space-y-1">
                  <p className="text-sm font-semibold">Entitlement Rules</p>
                  <p className="text-sm text-muted-foreground">
                    Define who gets how many days for this leave type.
                  </p>
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={handleAddRule}>
                  Add Rule
                </Button>
              </div>
              {entitlementRules.length === 0
                ? (
                  <p className="text-sm text-muted-foreground">No rules added yet.</p>
                )
                : (
                  <div className="space-y-4">
                    {entitlementRules.map((rule, index) => (
                      <div key={rule.rowId} className="space-y-3 rounded-lg border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-medium">
                            {`Rule #${index + 1}`}
                          </p>
                          {/* {rule.isExisting
                            ? (
                              <span className="text-xs text-muted-foreground">Existing rule</span>
                            )
                            : ( */}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveRule(rule.rowId)}
                          >
                            Remove
                          </Button>
                          {/* // )} */}
                        </div>
                        <div className="grid gap-4 md:grid-cols-6">
                          <div className="space-y-2 md:col-span-1">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`entitlement-days-${rule.rowId}`}>Days</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    aria-label="Days help"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Enter how many days are allowed. Use a whole number.
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <Input
                              id={`entitlement-days-${rule.rowId}`}
                              type="number"
                              min={0}
                              value={rule.days}
                              onChange={event => handleRuleChange(rule.rowId, { days: event.target.value })}
                              placeholder="20"
                            />
                          </div>
                          <div className="space-y-2 md:col-span-1">
                            <div className="flex items-center gap-2">
                              <Label htmlFor={`entitlement-year-${rule.rowId}`}>Year</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    aria-label="Year help"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Type the year for this rule, like 2025. Leave it empty to use any year.
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <Input
                              id={`entitlement-year-${rule.rowId}`}
                              type="number"
                              min={2000}
                              value={rule.year}
                              onChange={event => handleRuleChange(rule.rowId, { year: event.target.value })}
                              placeholder="2025"
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <SearchSelect
                              label="Gender"
                              placeholder="Search & select gender"
                              tooltipContent="Choose who this rule is for. Pick Male, Female, or Any for everyone."
                              options={[
                                { value: undefined, id: undefined, name: 'Any gender' },
                                { value: 'FEMALE', id: 'FEMALE', name: 'Female' },
                                { value: 'MALE', id: 'MALE', name: 'Male' },
                              ]}
                              value={rule.gender}
                              onValueChange={value => handleRuleChange(rule.rowId, {
                                gender: value === '' ? undefined : value as EntitlementRuleFormState['gender'],
                              })}
                            />
                          </div>
                          <div className="space-y-2 md:col-span-2">
                            <SearchSelect
                              label="Department"
                              placeholder="Search & select department"
                              tooltipContent="Select the department this rule applies to. Leave empty for all departments."
                              options={departments.map(item => ({
                                value: item.id?.toString() ?? '',
                                id: item.id?.toString() ?? '',
                                name: String(item.name ?? item.code ?? item.id ?? ''),
                              }))}
                              value={rule.departmentId}
                              onValueChange={value => handleRuleChange(rule.rowId, { departmentId: value })}
                            />
                          </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-6">
                          {/*
                            <div className="space-y-2 md:col-span-2">
                              <SearchSelect
                                label="User Type"
                                value={rule.userType}
                                tooltipContent="Select who this rule is for, like FULLTIME. This helps match the right people."
                                placeholder="Search & select user type"
                                options={[
                                  { value: undefined, name: 'Any user type' },
                                  ...entitlementUserTypes.map(userType => ({
                                    value: userType,
                                    id: userType,
                                    name: userType.replaceAll('_', ' '),
                                  })),
                                ]}
                                onValueChange={value => handleRuleChange(rule.rowId, {
                                  userType: value === '' ? undefined : value as EntitlementRuleFormState['userType'],
                                })}
                              />
                            </div>
                          */}
                          <div className="space-y-2 md:col-span-2">
                            <div className="flex items-center gap-2">
                              <Label>Supervision Level</Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                    aria-label="Supervision level help"
                                  >
                                    <Info className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Optional filter for supervision levels: Manager, Director, HR, CEO, or Non-supervisor.
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <MultipleSelector
                              placeholder="Search & select supervision levels"
                              options={entitlementSupervisionLevelOptions}
                              value={entitlementSupervisionLevelOptions.filter(option =>
                                rule.supervisionLevels.includes(option.value as LeaveEntitlementRuleCreateDtoSupervisionLevelsItem),
                              )}
                              onChange={options => handleRuleChange(rule.rowId, {
                                supervisionLevels: options.map(
                                  option => option.value as LeaveEntitlementRuleCreateDtoSupervisionLevelsItem,
                                ),
                              })}
                              emptyIndicator={(
                                <p className="px-2 py-2 text-sm text-muted-foreground">No supervision level found.</p>
                              )}
                              hidePlaceholderWhenSelected
                            />
                          </div>
                          <div className="flex items-center justify-between rounded-lg border px-3 py-2 md:col-span-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`entitlement-carry-over-${rule.rowId}`}>Carry Over</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                      aria-label="Carry over help"
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Turn this on if unused days can move to the next year.
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <p className="text-xs text-muted-foreground">Allow unused days to roll over.</p>
                            </div>
                            <AnimatedSwitch
                              id={`entitlement-carry-over-${rule.rowId}`}
                              checked={rule.carryOverAllowed}
                              onCheckedChange={checked => handleRuleChange(rule.rowId, { carryOverAllowed: checked })}
                            />
                          </div>
                          <div className="flex items-center justify-between rounded-lg border px-3 py-2 md:col-span-2">

                            <div>
                              <div className="flex items-center gap-2">
                                <Label htmlFor={`entitlement-weekends-${rule.rowId}`}>Weekends/Holidays</Label>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      type="button"
                                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                      aria-label="Weekends and holidays help"
                                    >
                                      <Info className="h-4 w-4" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Enable if weekends and holidays should count toward leave days.
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                              <p className="text-xs text-muted-foreground">Count weekends and holidays as leave.</p>
                            </div>
                            <AnimatedSwitch
                              id={`entitlement-weekends-${rule.rowId}`}
                              checked={rule.includeWeekendsAndHolidays}
                              onCheckedChange={checked => handleRuleChange(rule.rowId, {
                                includeWeekendsAndHolidays: checked,
                              })}
                            />
                          </div>
                        </div>
                        {rule.carryOverAllowed
                          ? (
                            <div className="grid gap-4 md:grid-cols-6">
                              <div className="space-y-2 md:col-span-1">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`entitlement-max-carryover-days-${rule.rowId}`}>Max Carryover</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                        aria-label="Max carryover help"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Maximum number of unused days this rule can carry into next year.
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input
                                  id={`entitlement-max-carryover-days-${rule.rowId}`}
                                  type="number"
                                  min={0}
                                  value={rule.maxCarryoverDays}
                                  onChange={event => handleRuleChange(rule.rowId, { maxCarryoverDays: event.target.value })}
                                  placeholder="10"
                                />
                              </div>
                              <div className="space-y-2 md:col-span-1">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`entitlement-carryover-expiry-month-${rule.rowId}`}>Expiry Month</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                        aria-label="Carryover expiry month help"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Month when carried-over days expire, for example 3 for March.
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input
                                  id={`entitlement-carryover-expiry-month-${rule.rowId}`}
                                  type="number"
                                  min={1}
                                  max={12}
                                  value={rule.carryoverExpiryMonth}
                                  onChange={event => handleRuleChange(rule.rowId, { carryoverExpiryMonth: event.target.value })}
                                  placeholder="3"
                                />
                              </div>
                              <div className="space-y-2 md:col-span-1">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`entitlement-carryover-expiry-day-${rule.rowId}`}>Expiry Day</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                        aria-label="Carryover expiry day help"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Day of the month when carryover expires, for example 31.
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input
                                  id={`entitlement-carryover-expiry-day-${rule.rowId}`}
                                  type="number"
                                  min={1}
                                  max={31}
                                  value={rule.carryoverExpiryDay}
                                  onChange={event => handleRuleChange(rule.rowId, { carryoverExpiryDay: event.target.value })}
                                  placeholder="31"
                                />
                              </div>
                              <div className="flex items-center justify-between rounded-lg border px-3 py-2 md:col-span-2">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Label htmlFor={`entitlement-borrowing-allowed-${rule.rowId}`}>Borrowing Allowed</Label>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                          aria-label="Borrowing allowed help"
                                        >
                                          <Info className="h-4 w-4" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        Allow this rule to approve requests that go below zero by borrowing from next year.
                                      </TooltipContent>
                                    </Tooltip>
                                  </div>
                                  <p className="text-xs text-muted-foreground">Allow borrowing from next year for this rule.</p>
                                </div>
                                <AnimatedSwitch
                                  id={`entitlement-borrowing-allowed-${rule.rowId}`}
                                  checked={rule.borrowingAllowed}
                                  onCheckedChange={checked => handleRuleChange(rule.rowId, { borrowingAllowed: checked })}
                                />
                              </div>
                              <div className="space-y-2 md:col-span-1">
                                <div className="flex items-center gap-2">
                                  <Label htmlFor={`entitlement-max-borrow-days-${rule.rowId}`}>Max Borrow</Label>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        type="button"
                                        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                                        aria-label="Max borrow help"
                                      >
                                        <Info className="h-4 w-4" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Maximum days this rule can borrow from next year when borrowing is enabled.
                                    </TooltipContent>
                                  </Tooltip>
                                </div>
                                <Input
                                  id={`entitlement-max-borrow-days-${rule.rowId}`}
                                  type="number"
                                  min={0}
                                  value={rule.maxBorrowDays}
                                  onChange={event => handleRuleChange(rule.rowId, { maxBorrowDays: event.target.value })}
                                  placeholder="5"
                                  disabled={!rule.borrowingAllowed}
                                />
                              </div>
                            </div>
                          )
                          : null}
                      </div>
                    ))}
                  </div>
                )}
              <p className="text-xs text-muted-foreground">Rules without days are ignored.</p>
            </div>
            {departmentsQuery.isError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to load departments</AlertTitle>
                  <AlertDescription>{departmentsQuery.error?.message ?? 'Please try again.'}</AlertDescription>
                </Alert>
              )
              : null}
            {entitlementError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Check entitlement rules</AlertTitle>
                  <AlertDescription>{entitlementError}</AlertDescription>
                </Alert>
              )
              : null}
            {submitError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to save leave type</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )
              : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || isSaving}>
                {isSaving ? 'Saving...' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <CardTitle>Leave Types and Entitlements</CardTitle>
            <CardDescription>Review leave types alongside entitlement rules.</CardDescription>
          </div>
          <Button type="button" onClick={openCreateDialog}>
            Create Leave Type
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {isTableLoading
            ? (
              <p className="text-sm text-muted-foreground">Loading leave types and entitlements...</p>
            )
            : null}
          {leaveTypesQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load leave types</AlertTitle>
                <AlertDescription>{leaveTypesQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {departmentsQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load departments</AlertTitle>
                <AlertDescription>{departmentsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          {showOrganisationFilter && organisationsQuery.isError
            ? (
              <Alert variant="destructive">
                <AlertTitle>Unable to load organisations</AlertTitle>
                <AlertDescription>{organisationsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
              </Alert>
            )
            : null}
          <EnhancedDataTable
            data={filteredTableRows}
            columns={columns}
            loading={isTableLoading}
            emptyStateMessage="No leave types or entitlements found."
            enableColumnFilters={false}
            enableExport={false}
            initialColumnVisibility={initialColumnVisibility}
            customButtons={showOrganisationFilter
              ? (
                <OrganisationSearchInput
                  label=""
                  placeholder="Select an organisation"
                  value={organisationFilter}
                  onValueChange={setOrganisationFilter}
                  disabled={organisationsQuery.isPending || organisationsQuery.isError}
                  options={[
                    { value: 'all', id: 'all', name: 'All organisations' },
                    ...organisations
                      .filter(org => typeof org.id === 'number')
                      .map(org => ({
                        value: String(org.id),
                        id: org.id,
                        name: org.name ?? `Organisation ${org.id}`,
                      })),
                  ]}
                  isLoading={organisationsQuery.isPending}
                />
              )
              : undefined}
          />
        </CardContent>
      </Card>
    </div>
  )
}
